import { OrdersAvailableReportRow } from '../model/orders-available.model';
import {
  ORDERS_REPORT_STATUS_OPTIONS,
  ORDERS_REPORT_YEAR_OPTIONS,
  formatOrdersReportStatus
} from './orders-report.utils';

export interface ReservationsReportFiltersState {
  searchValue: string;
  yearFile: string | null;
  providerName: string | null;
  reservationStatus: string | null;
  orderStatus: string | null;
}

export interface ReservationsReportFilterOptions {
  years: string[];
  providers: string[];
  reservationStatuses: string[];
  orderStatuses: string[];
}

export interface ReservationsReportFilterChip {
  id: keyof ReservationsReportFiltersState;
  label: string;
}

export const EMPTY_RESERVATIONS_REPORT_FILTERS: ReservationsReportFiltersState =
  {
    searchValue: '',
    yearFile: null,
    providerName: null,
    reservationStatus: null,
    orderStatus: null
  };

export const RESERVATIONS_REPORT_STATUS_OPTIONS = [
  'EN ESPERA',
  'CONFIRMADO',
  'RECHAZADO',
  'ELIMINADO'
] as const;

export const RESERVATIONS_REPORT_COLUMNS = [
  'fileNumber',
  'numberOS',
  'nameProveedor',
  'reservationNumber',
  'reservationStatus',
  'estadoOS',
  'serviceType',
  'scheduledPaymentDate',
  'totalGeneral',
  'currency',
  'actions'
] as const;

export function buildReservationsReportFilterOptions(
  rows: OrdersAvailableReportRow[]
): ReservationsReportFilterOptions {
  const list = rows ?? [];
  return {
    years: [...ORDERS_REPORT_YEAR_OPTIONS],
    providers: [
      ...new Set(list.map((row) => row.nameProveedor).filter(Boolean))
    ].sort() as string[],
    reservationStatuses: [...RESERVATIONS_REPORT_STATUS_OPTIONS],
    orderStatuses: [...ORDERS_REPORT_STATUS_OPTIONS]
  };
}

export function buildReservationsReportFilterChips(
  filters: ReservationsReportFiltersState
): ReservationsReportFilterChip[] {
  const chips: ReservationsReportFilterChip[] = [];

  if (filters.searchValue?.trim()) {
    chips.push({
      id: 'searchValue',
      label: `Buscar: ${filters.searchValue.trim()}`
    });
  }
  if (filters.yearFile) {
    chips.push({ id: 'yearFile', label: `Año: ${filters.yearFile}` });
  }
  if (filters.providerName) {
    chips.push({
      id: 'providerName',
      label: `Proveedor: ${filters.providerName}`
    });
  }
  if (filters.reservationStatus) {
    chips.push({
      id: 'reservationStatus',
      label: `Reserva: ${filters.reservationStatus}`
    });
  }
  if (filters.orderStatus) {
    chips.push({ id: 'orderStatus', label: `OS: ${filters.orderStatus}` });
  }

  return chips;
}

export function countActiveReservationsReportFilters(
  filters: ReservationsReportFiltersState
): number {
  return buildReservationsReportFilterChips(filters).length;
}

export function removeReservationsReportFilter(
  filters: ReservationsReportFiltersState,
  chipId: ReservationsReportFilterChip['id']
): ReservationsReportFiltersState {
  switch (chipId) {
    case 'searchValue':
      return { ...filters, searchValue: '' };
    case 'yearFile':
      return { ...filters, yearFile: null };
    case 'providerName':
      return { ...filters, providerName: null };
    case 'reservationStatus':
      return { ...filters, reservationStatus: null };
    case 'orderStatus':
      return { ...filters, orderStatus: null };
    default:
      return filters;
  }
}

export function filterReservationsReportRows(
  rows: OrdersAvailableReportRow[],
  filters: ReservationsReportFiltersState
): OrdersAvailableReportRow[] {
  const search = filters.searchValue?.trim().toLowerCase() ?? '';
  const terms = search ? search.split(/\s+/).filter(Boolean) : [];

  return (rows ?? []).filter((row) => {
    if (terms.length) {
      const haystack = [
        row.numberFile,
        row.fileExtend,
        row.numberOS,
        row.estadoOS,
        row.reservationNumber,
        row.reservationStatus,
        row.nameProveedor,
        row.paymentCondition,
        row.currency,
        ...(row.serviceTypes ?? [])
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      if (!terms.every((term) => haystack.includes(term))) {
        return false;
      }
    }

    if (
      filters.yearFile &&
      !String(row.numberFile ?? '')
        .toLowerCase()
        .includes(filters.yearFile.toLowerCase())
    ) {
      return false;
    }
    if (filters.providerName && row.nameProveedor !== filters.providerName) {
      return false;
    }
    if (
      filters.reservationStatus &&
      formatOrdersReportStatus(row.reservationStatus) !==
        filters.reservationStatus &&
      row.reservationStatus !== filters.reservationStatus
    ) {
      return false;
    }
    if (filters.orderStatus && row.estadoOS !== filters.orderStatus) {
      return false;
    }

    return true;
  });
}
